import React, { ReactElement, useEffect, useState } from 'react'
import {
  NativeSyntheticEvent,
  Platform,
  Pressable,
  PressableProps,
  StyleProp,
  StyleSheet,
  TargetedEvent,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native'

import { Href, Link } from 'zens-router'

import { Color, Spacing } from '../../style'
import { Icon, IconName } from '../icon'
import { Text } from '../typography'

const styles = StyleSheet.create({
  container: {},
  content: {
    flex: 1,
    minHeight: 40,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRightWidth: 3,
    borderRightColor: Color.transparent,
  },
  icon: {
    marginHorizontal: Spacing.S,
    width: 20,
    height: 20,
  },
  noIcon: {
    marginLeft: Spacing.M,
  },
  text: {
    flex: 1,
    marginHorizontal: Spacing.S,
    fontSize: 14,
  },
  focussed: {
    backgroundColor: Color.selectedBlue,
    borderRightColor: Color.greyBlue,
  },
  pressed: {
    backgroundColor: Color.lightGreyBlue,
    borderRightColor: Color.greyBlue,
  },
  selected: {
    backgroundColor: Color.lightGreyBlue,
    borderRightColor: Color.greyBlue,
  },
  activeIcon: {
    color: Color.darkGreyBlue,
  },
  activeText: {
    color: Color.darkGreyBlue,
  },
  indentedText: {
    fontSize: 14,
  },
  hovered: {
    backgroundColor: Color.sleepingGrey,
    borderRightColor: Color.calmGrey,
  },
  disabled: {
    backgroundColor: Color.sleepingGrey,
  },
  disabledIcon: {
    color: Color.calmGrey,
  },
  disabledText: {
    color: Color.calmGrey,
  },
  openIcon: {
    marginHorizontal: Spacing.S,
    width: 10,
    height: 10,
  },
})

export interface Props extends Omit<PressableProps, 'style'> {
  title: string
  icon?: IconName | ReactElement
  selected?: boolean // Item is marked with selected background and sub items opened
  active?: boolean // Item is marked with selected text and sub items opened
  opened?: boolean // Item is not marked but sub items opened
  hasItems?: boolean
  style?: StyleProp<ViewStyle>
  textStyle?: StyleProp<TextStyle>
  iconStyle?: StyleProp<ViewStyle>
  unfolded?: boolean
  indentation?: number
  accessory?: IconName | ReactElement
  href?: Href
}
const MenuItem = (props: Props) => {
  const {
    title,
    icon,
    selected,
    active,
    opened,
    hasItems,
    style,
    textStyle,
    iconStyle,
    unfolded,
    indentation,
    accessory,
    disabled,
    onFocus,
    onBlur,
    href,
    ...otherProps
  } = props
  const [focussed, setFocussed] = useState(false)

  const updateActive = (value: boolean) => (event: NativeSyntheticEvent<TargetedEvent>) => {
    setFocussed(value)
    value && onFocus && onFocus(event)
    !value && onBlur && onBlur(event)
  }

  useEffect(() => {
    if (disabled) setFocussed(false)
  }, [disabled])

  const renderOpen = () => {
    if (!hasItems || unfolded) return null
    return <Icon name={active || opened ? 'up' : 'down'} style={styles.openIcon} />
  }

  const renderItem = (state: { pressed: boolean; hovered?: boolean }) => {
    const { pressed, hovered } = state

    const indentationStyle = !!indentation && {
      paddingLeft: Spacing.L * indentation,
    }
    const contentStyles = [
      styles.content,
      indentationStyle,
      focussed && styles.focussed,
      hovered && styles.hovered,
      selected && styles.selected,
      pressed && styles.pressed,
      disabled && styles.disabled,
    ]

    const iconStyles = [
      styles.icon,
      (pressed || selected || active) && styles.activeIcon,
      disabled && styles.disabledIcon,
      iconStyle,
    ]

    const accessoryStyles = [
      styles.icon,
      (pressed || selected || active) && styles.activeIcon,
      disabled && styles.disabledIcon,
    ]

    const textStyles = [
      styles.text,
      (pressed || selected || active) && styles.activeText,
      disabled && styles.disabledText,
      !!indentation && styles.indentedText,
      textStyle,
    ]
    return (
      <View style={contentStyles}>
        {typeof icon === 'string' ? (
          <Icon name={icon} style={iconStyles} />
        ) : (
          <View style={!!indentation && styles.noIcon}>{icon}</View>
        )}
        {!unfolded && (
          <Text style={textStyles} level={selected || active ? 5 : 6}>
            {title}
          </Text>
        )}
        {typeof accessory === 'string' ? (
          <Icon name={accessory} style={accessoryStyles} />
        ) : (
          accessory && <View style={!!indentation && styles.noIcon}>{accessory}</View>
        )}
        {renderOpen()}
      </View>
    )
  }

  return href ? (
    <Link
      href={href}
      asChild
      style={[
        styles.container,
        Platform.select({
          // @ts-expect-error outline style none is not defined
          web: {
            outlineStyle: 'none',
          } as ViewStyle,
        }),
        style,
      ]}
    >
      <Pressable disabled={disabled} onFocus={updateActive(true)} onBlur={updateActive(false)} {...otherProps}>
        {renderItem}
      </Pressable>
    </Link>
  ) : (
    <Pressable
      disabled={disabled}
      onFocus={updateActive(true)}
      onBlur={updateActive(false)}
      style={[
        styles.container,
        Platform.select({
          // @ts-expect-error outline style none is not defined
          web: {
            outlineStyle: 'none',
          } as ViewStyle,
        }),
        style,
      ]}
      {...otherProps}
    >
      {renderItem}
    </Pressable>
  )
}

export default MenuItem
